import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Tabs,
    Tab,
    TextField,
    Button,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    Autocomplete,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Download as DownloadIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import reportService from '../../../services/reportService';
import { bookingService } from '../../../services/bookingService';
import branchService from '../../../services/branchService';
import debounce from 'lodash/debounce';

/**
 * Credit and instant booking GST reports, as they were before the report
 * catalogue existed.
 */
const GstReports = () => {
    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Credit Booking Report State
    const [creditFromDate, setCreditFromDate] = useState('');
    const [creditToDate, setCreditToDate] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerOptions, setCustomerOptions] = useState([]);
    const [customerSearchText, setCustomerSearchText] = useState('');
    const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
    const [creditBranch, setCreditBranch] = useState('');
    const [creditReportData, setCreditReportData] = useState(null);

    // Instant Booking Report State
    const [instantFromDate, setInstantFromDate] = useState('');
    const [instantToDate, setInstantToDate] = useState('');
    const [gstNo, setGstNo] = useState('');
    const [instantBranch, setInstantBranch] = useState('');
    const [instantPaymentType, setInstantPaymentType] = useState('');
    const [instantReportData, setInstantReportData] = useState(null);

    // Branch options shared by both tabs
    const [branchOptions, setBranchOptions] = useState([]);

    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const response = await branchService.getActiveBranches();
                setBranchOptions(response?.data || []);
            } catch (err) {
                console.error('Error fetching branches:', err);
                setBranchOptions([]);
            }
        };
        fetchBranches();
    }, []);

    // Customer search with debouncing
    const searchCustomers = useCallback(async (searchText) => {
        if (!searchText || searchText.length < 2) {
            setCustomerOptions([]);
            return;
        }

        setCustomerSearchLoading(true);
        try {
            const response = await bookingService.searchCustomers(searchText);
            if (response.success) {
                setCustomerOptions(response.data || []);
            }
        } catch (err) {
            console.error('Error searching customers:', err);
            setCustomerOptions([]);
        } finally {
            setCustomerSearchLoading(false);
        }
    }, []);

    const debouncedSearchCustomers = useMemo(
        () => debounce(searchCustomers, 300),
        [searchCustomers]
    );

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        setError(null);
    };

    // Credit Booking Report Handlers
    const handleGenerateCreditReport = async () => {
        if (!creditFromDate || !creditToDate) {
            setError('Please select both from and to dates');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await reportService.getCreditBookingGstReport(
                creditFromDate,
                creditToDate,
                selectedCustomer?.id,
                creditBranch || null
            );
            setCreditReportData(response.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to generate report');
        } finally {
            setLoading(false);
        }
    };

    const handleExportCreditReport = async () => {
        if (!creditFromDate || !creditToDate) {
            setError('Please select both from and to dates');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            await reportService.exportCreditBookingGstReport(
                creditFromDate,
                creditToDate,
                selectedCustomer?.id,
                creditBranch || null
            );
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to export report');
        } finally {
            setLoading(false);
        }
    };

    // Instant Booking Report Handlers
    const handleGenerateInstantReport = async () => {
        if (!instantFromDate || !instantToDate) {
            setError('Please select both from and to dates');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await reportService.getInstantBookingGstReport(
                instantFromDate,
                instantToDate,
                gstNo,
                instantBranch || null,
                instantPaymentType || null
            );
            setInstantReportData(response.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to generate report');
        } finally {
            setLoading(false);
        }
    };

    const handleExportInstantReport = async () => {
        if (!instantFromDate || !instantToDate) {
            setError('Please select both from and to dates');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            await reportService.exportInstantBookingGstReport(
                instantFromDate,
                instantToDate,
                gstNo,
                instantBranch || null,
                instantPaymentType || null
            );
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to export report');
        } finally {
            setLoading(false);
        }
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount);
    };

    return (
        <Paper sx={{ width: '100%', mb: 2 }}>
            <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tab label="Credit Booking GST Report" />
                <Tab label="Instant Booking GST Report" />
            </Tabs>

            {error && (
                <Box sx={{ p: 2 }}>
                    <Alert severity="error" onClose={() => setError(null)}>
                        {error}
                    </Alert>
                </Box>
            )}

            {/* Credit Booking Report Tab */}
            {tabValue === 0 && (
                <Box sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={3}>
                            <TextField
                                label="From Date"
                                type="date"
                                value={creditFromDate}
                                onChange={(e) => setCreditFromDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                label="To Date"
                                type="date"
                                value={creditToDate}
                                onChange={(e) => setCreditToDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel id="credit-branch-label">Branch</InputLabel>
                                <Select
                                    labelId="credit-branch-label"
                                    label="Branch"
                                    value={creditBranch}
                                    onChange={(e) => setCreditBranch(e.target.value)}
                                >
                                    <MenuItem value="">All Branches</MenuItem>
                                    {branchOptions.map((branch) => (
                                        <MenuItem key={branch.branch_id} value={branch.branch_id}>
                                            {branch.branch_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Autocomplete
                                options={customerOptions}
                                getOptionLabel={(option) => `${option.cust_name} (${option.cust_code}) - ${option.from_company || 'N/A'}`}
                                loading={customerSearchLoading}
                                value={selectedCustomer}
                                inputValue={customerSearchText}
                                onInputChange={(event, newInputValue) => {
                                    setCustomerSearchText(newInputValue);
                                    debouncedSearchCustomers(newInputValue);
                                }}
                                onChange={(event, newValue) => setSelectedCustomer(newValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search Customer (Optional)"
                                        placeholder="Type to search customers..."
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                                            endAdornment: customerSearchLoading ? <CircularProgress size={20} /> : params.InputProps.endAdornment,
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props}>
                                        <Box>
                                            <Typography variant="body2" fontWeight="bold">
                                                {option.cust_name} ({option.cust_code})
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {option.from_company} • GST: {option.cust_gst_no || 'N/A'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                                noOptionsText={customerSearchText.length < 2 ? "Type at least 2 characters to search" : "No customers found"}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Button
                                variant="contained"
                                onClick={handleGenerateCreditReport}
                                disabled={loading}
                                fullWidth
                                sx={{ height: '56px' }}
                            >
                                {loading ? <CircularProgress size={24} /> : 'Generate Report'}
                            </Button>
                        </Grid>
                    </Grid>

                    {creditReportData && (
                        <Box sx={{ mt: 4 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Box>
                                    <Typography variant="h6">
                                        Report: {creditReportData.from_date} to {creditReportData.to_date}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Branch: {creditReportData.branch_name || 'All Branches'}
                                    </Typography>
                                </Box>
                                <Button
                                    variant="outlined"
                                    startIcon={<DownloadIcon />}
                                    onClick={handleExportCreditReport}
                                >
                                    Export to Excel
                                </Button>
                            </Box>

                            {/* Summary Card */}
                            <Card sx={{ mb: 3, bgcolor: 'primary.light', color: 'white' }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>Summary</Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6} md={2}>
                                            <Typography variant="body2">Total Invoices</Typography>
                                            <Typography variant="h6">{creditReportData.summary.total_invoices}</Typography>
                                        </Grid>
                                        <Grid item xs={6} md={2}>
                                            <Typography variant="body2">Total Bookings</Typography>
                                            <Typography variant="h6">{creditReportData.summary.total_bookings}</Typography>
                                        </Grid>
                                        <Grid item xs={6} md={2}>
                                            <Typography variant="body2">Total Amount</Typography>
                                            <Typography variant="h6">{formatCurrency(creditReportData.summary.total_amount)}</Typography>
                                        </Grid>
                                        <Grid item xs={6} md={2}>
                                            <Typography variant="body2">SGST</Typography>
                                            <Typography variant="h6">{formatCurrency(creditReportData.summary.total_sgst)}</Typography>
                                        </Grid>
                                        <Grid item xs={6} md={2}>
                                            <Typography variant="body2">CGST</Typography>
                                            <Typography variant="h6">{formatCurrency(creditReportData.summary.total_cgst)}</Typography>
                                        </Grid>
                                        <Grid item xs={6} md={2}>
                                            <Typography variant="body2">Grand Total</Typography>
                                            <Typography variant="h6">{formatCurrency(creditReportData.summary.grand_total)}</Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>

                            {/* Customer-wise Report */}
                            {creditReportData.customers.map((customer, index) => (
                                <Accordion key={index} defaultExpanded={creditReportData.customers.length === 1}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', pr: 2 }}>
                                            <Typography variant="h6">
                                                {customer.customer_code} - {customer.customer_name}
                                            </Typography>
                                            <Typography variant="h6" color="primary">
                                                {formatCurrency(customer.totals.grand_total)}
                                            </Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box>
                                            <Typography variant="body2" gutterBottom>
                                                <strong>GST No:</strong> {customer.gst_no || 'N/A'}
                                            </Typography>
                                            <Typography variant="body2" gutterBottom>
                                                <strong>Company/Division:</strong> {customer.from_company}
                                            </Typography>
                                            <Typography variant="body2" gutterBottom>
                                                <strong>Address:</strong> {customer.from_address}, {customer.from_city}, {customer.from_state} - {customer.from_pincode}
                                            </Typography>

                                            <TableContainer sx={{ mt: 2 }}>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Invoice No</TableCell>
                                                            <TableCell>Date</TableCell>
                                                            <TableCell>Period</TableCell>
                                                            <TableCell align="right">Shipments</TableCell>
                                                            <TableCell align="right">Freight</TableCell>
                                                            <TableCell align="right">Fuel Surcharge</TableCell>
                                                            <TableCell align="right">Other Charges</TableCell>
                                                            <TableCell align="right">Service Tax</TableCell>
                                                            <TableCell align="right">Total</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {customer.invoices.map((invoice) => (
                                                            <TableRow key={invoice.invoice_id}>
                                                                <TableCell>{invoice.invoice_no}</TableCell>
                                                                <TableCell>{new Date(invoice.invoice_date).toLocaleDateString()}</TableCell>
                                                                <TableCell>
                                                                    {new Date(invoice.from_date).toLocaleDateString()} - {new Date(invoice.to_date).toLocaleDateString()}
                                                                </TableCell>
                                                                <TableCell align="right">{invoice.total_shipments}</TableCell>
                                                                <TableCell align="right">{formatCurrency(invoice.total_freight)}</TableCell>
                                                                <TableCell align="right">{formatCurrency(invoice.total_fuel_surcharge)}</TableCell>
                                                                <TableCell align="right">{formatCurrency(invoice.total_other_charges)}</TableCell>
                                                                <TableCell align="right">{formatCurrency(invoice.total_service_tax)}</TableCell>
                                                                <TableCell align="right"><strong>{formatCurrency(invoice.grand_total)}</strong></TableCell>
                                                            </TableRow>
                                                        ))}
                                                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                                                            <TableCell colSpan={3}><strong>Customer Total</strong></TableCell>
                                                            <TableCell align="right"><strong>{customer.totals.booking_count}</strong></TableCell>
                                                            <TableCell align="right"><strong>{formatCurrency(customer.totals.total_freight)}</strong></TableCell>
                                                            <TableCell align="right"><strong>{formatCurrency(customer.totals.total_fuel_surcharge)}</strong></TableCell>
                                                            <TableCell align="right"><strong>{formatCurrency(customer.totals.total_other_charges)}</strong></TableCell>
                                                            <TableCell align="right"><strong>{formatCurrency(customer.totals.total_service_tax)}</strong></TableCell>
                                                            <TableCell align="right"><strong>{formatCurrency(customer.totals.grand_total)}</strong></TableCell>
                                                        </TableRow>
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Box>
                    )}
                </Box>
            )}

            {/* Instant Booking Report Tab */}
            {tabValue === 1 && (
                <Box sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={3}>
                            <TextField
                                label="From Date"
                                type="date"
                                value={instantFromDate}
                                onChange={(e) => setInstantFromDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                label="To Date"
                                type="date"
                                value={instantToDate}
                                onChange={(e) => setInstantToDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel id="instant-branch-label">Branch</InputLabel>
                                <Select
                                    labelId="instant-branch-label"
                                    label="Branch"
                                    value={instantBranch}
                                    onChange={(e) => setInstantBranch(e.target.value)}
                                >
                                    <MenuItem value="">All Branches</MenuItem>
                                    {branchOptions.map((branch) => (
                                        <MenuItem key={branch.branch_id} value={branch.branch_id}>
                                            {branch.branch_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel id="instant-payment-type-label">Payment Type</InputLabel>
                                <Select
                                    labelId="instant-payment-type-label"
                                    label="Payment Type"
                                    value={instantPaymentType}
                                    onChange={(e) => setInstantPaymentType(e.target.value)}
                                >
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="paid">Paid (Shipper)</MenuItem>
                                    <MenuItem value="to_pay">To Pay (Recipient)</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                label="GST Number (Optional)"
                                value={gstNo}
                                onChange={(e) => setGstNo(e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Button
                                variant="contained"
                                onClick={handleGenerateInstantReport}
                                disabled={loading}
                                fullWidth
                                sx={{ height: '56px' }}
                            >
                                {loading ? <CircularProgress size={24} /> : 'Generate Report'}
                            </Button>
                        </Grid>
                    </Grid>

                    {instantReportData && (
                        <Box sx={{ mt: 4 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Box>
                                    <Typography variant="h6">
                                        Report: {instantReportData.from_date} to {instantReportData.to_date}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Branch: {instantReportData.branch_name || 'All Branches'}
                                        {' • '}
                                        Payment: {instantReportData.payment_type === 'paid'
                                            ? 'Paid'
                                            : instantReportData.payment_type === 'to_pay'
                                                ? 'To Pay'
                                                : 'All'}
                                    </Typography>
                                </Box>
                                <Button
                                    variant="outlined"
                                    startIcon={<DownloadIcon />}
                                    onClick={handleExportInstantReport}
                                >
                                    Export to Excel
                                </Button>
                            </Box>

                            {/* Summary Card */}
                            <Card sx={{ mb: 3, bgcolor: 'secondary.light', color: 'white' }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>Summary</Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6} md={1.7}>
                                            <Typography variant="body2">Total Bookings</Typography>
                                            <Typography variant="h6">{instantReportData.summary.total_bookings}</Typography>
                                        </Grid>
                                        <Grid item xs={6} md={1.7}>
                                            <Typography variant="body2">Paid</Typography>
                                            <Typography variant="h6">{instantReportData.summary.paid_bookings}</Typography>
                                        </Grid>
                                        <Grid item xs={6} md={1.7}>
                                            <Typography variant="body2">To Pay</Typography>
                                            <Typography variant="h6">{instantReportData.summary.to_pay_bookings}</Typography>
                                        </Grid>
                                        <Grid item xs={6} md={1.7}>
                                            <Typography variant="body2">Total Amount</Typography>
                                            <Typography variant="h6">{formatCurrency(instantReportData.summary.total_amount)}</Typography>
                                        </Grid>
                                        <Grid item xs={6} md={1.7}>
                                            <Typography variant="body2">SGST</Typography>
                                            <Typography variant="h6">{formatCurrency(instantReportData.summary.total_sgst)}</Typography>
                                        </Grid>
                                        <Grid item xs={6} md={1.7}>
                                            <Typography variant="body2">CGST</Typography>
                                            <Typography variant="h6">{formatCurrency(instantReportData.summary.total_cgst)}</Typography>
                                        </Grid>
                                        <Grid item xs={6} md={1.8}>
                                            <Typography variant="body2">Grand Total</Typography>
                                            <Typography variant="h6">{formatCurrency(instantReportData.summary.grand_total)}</Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>

                            {/* GST-wise Report */}
                            {instantReportData.gst_groups.map((gstGroup, index) => (
                                <Accordion key={index} defaultExpanded={instantReportData.gst_groups.length === 1}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', pr: 2 }}>
                                            <Box>
                                                <Typography variant="h6">
                                                    GST: {gstGroup.gst_no}
                                                </Typography>
                                                {gstGroup.customer_name && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {gstGroup.customer_name}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Typography variant="h6" color="secondary">
                                                {formatCurrency(gstGroup.totals.grand_total)}
                                            </Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>AWB No</TableCell>
                                                        <TableCell>Date</TableCell>
                                                        <TableCell>From</TableCell>
                                                        <TableCell>To</TableCell>
                                                        <TableCell>Payment</TableCell>
                                                        <TableCell align="right">Freight</TableCell>
                                                        <TableCell align="right">SGST</TableCell>
                                                        <TableCell align="right">CGST</TableCell>
                                                        <TableCell align="right">Total</TableCell>
                                                        <TableCell align="right">Extra Charges</TableCell>
                                                        <TableCell align="right">Grand Total</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {gstGroup.bookings.map((booking) => (
                                                        <TableRow key={booking.booking_id}>
                                                            <TableCell>{booking.awb_no}</TableCell>
                                                            <TableCell>{new Date(booking.booking_date).toLocaleDateString()}</TableCell>
                                                            <TableCell>{booking.from_city}</TableCell>
                                                            <TableCell>{booking.to_city}</TableCell>
                                                            <TableCell>{booking.instant_payment_type}</TableCell>
                                                            <TableCell align="right">{formatCurrency(booking.basic_freight)}</TableCell>
                                                            <TableCell align="right">{formatCurrency(booking.sgst_amount)}</TableCell>
                                                            <TableCell align="right">{formatCurrency(booking.cgst_amount)}</TableCell>
                                                            <TableCell align="right">{formatCurrency(booking.total_with_gst)}</TableCell>
                                                            <TableCell align="right">{formatCurrency(booking.extra_charges)}</TableCell>
                                                            <TableCell align="right">{formatCurrency(booking.grand_total)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                                                        <TableCell colSpan={5}><strong>GST Group Total</strong></TableCell>
                                                        <TableCell align="right"><strong>{formatCurrency(gstGroup.totals.total_freight)}</strong></TableCell>
                                                        <TableCell align="right"><strong>{formatCurrency(gstGroup.totals.total_sgst)}</strong></TableCell>
                                                        <TableCell align="right"><strong>{formatCurrency(gstGroup.totals.total_cgst)}</strong></TableCell>
                                                        <TableCell align="right"><strong>{formatCurrency(gstGroup.totals.total_with_gst)}</strong></TableCell>
                                                        <TableCell align="right"><strong>{formatCurrency(gstGroup.totals.total_extra_charges)}</strong></TableCell>
                                                        <TableCell align="right"><strong>{formatCurrency(gstGroup.totals.grand_total)}</strong></TableCell>
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Box>
                    )}
                </Box>
            )}
        </Paper>
    );
};

export default GstReports;
